import React from 'react';
import { AlertTriangle, CheckCircle, Info, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ArxivLicense } from '@/types/license';

interface LicenseStatusDisplayProps {
  license?: ArxivLicense;
  isCompliant?: boolean;
  isLoading?: boolean;
  error?: string;
}

export const LicenseStatusDisplay: React.FC<LicenseStatusDisplayProps> = ({
  license,
  isCompliant,
  isLoading,
  error
}) => {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-sm text-muted-foreground">Checking license compliance...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="default" className="bg-yellow-50/50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-900">
        <Info className="h-4 w-4" />
        <AlertTitle>License Check Warning</AlertTitle>
        <AlertDescription className="text-sm">
          Could not verify license compliance: {error}
          <br />
          <span className="text-xs text-muted-foreground mt-1 block">
            You may proceed, but the paper will be subject to manual review.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  if (!license) {
    return null;
  }

  const isNonCompliant = isCompliant === false;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">License Information</span>
          {isCompliant === true && (
            <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              <CheckCircle className="h-3 w-3 mr-1" />
              Compliant
            </Badge>
          )}
          {isNonCompliant && (
            <Badge variant="destructive" className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Non-Compliant
            </Badge>
          )}
        </div>
      </div>

      <div className="bg-background rounded-md border p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{license.name}</span>
          {license.url && (
            <a
              href={license.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              View License <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {license.restrictions && license.restrictions.length > 0 && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Restrictions:</span>
            <div className="space-y-1">
              {license.restrictions.map((restriction, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <div className={`h-1.5 w-1.5 rounded-full ${
                    restriction.type === 'non-commercial' || restriction.type === 'no-derivatives' 
                      ? 'bg-red-500' 
                      : 'bg-yellow-500'
                  }`} />
                  <span className="text-muted-foreground">{restriction.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isNonCompliant && (
        <Alert variant="destructive" className="bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>License Not Compliant</AlertTitle>
          <AlertDescription className="text-sm">
            This paper uses a <strong>{license.name}</strong> license, which restricts commercial use and derivative works. 
            Papers with these restrictions cannot be uploaded to maintain open academic sharing.
            <br />
            <span className="text-xs text-muted-foreground mt-2 block">
              Consider choosing a paper with a more permissive license like CC BY, CC BY-SA, or CC BY-NC.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {isCompliant === true && (
        <div className="bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 px-3 py-2 rounded-md text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          <span>This license is compatible with our platform's open sharing policy.</span>
        </div>
      )}
    </div>
  );
};